import { useCallback, useEffect, useRef, useState } from 'react'
import { getApiOrigin } from '@/lib/sdk-official/loader'

/**
 * Tells an open tab that it is running an old version (F-61).
 *
 * Two sources, two levels:
 * - **recommended**: the app was deployed again. `/version.json`, served by
 *   the app itself, names a commit other than the one this bundle was built
 *   from.
 * - **required**: the API's contract changed (its signature moved), or a piece
 *   of this bundle failed to load. Either way some actions may fail until the
 *   page is reloaded.
 *
 * The API's own commit is never a reason to warn: a deploy that keeps the
 * contract changes nothing for an open tab.
 *
 * The API route may not exist (it ships separately). When it is missing or
 * fails, the contract check is skipped in silence: no error on screen, nothing
 * logged, nothing sent to Sentry.
 */

/** The API's public version route: `{ commit, contract }`, bare or in `data`. */
export const API_VERSION_PATH = '/api/version'
export const APP_VERSION_PATH = '/version.json'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
/** A burst of focus and online events must not become a burst of requests. */
const MIN_GAP_MS = 15 * 1000
/** "Later" on a required update holds it back this long, never longer. */
export const REQUIRED_SNOOZE_MS = 30 * 60 * 1000

export type UpdateLevel = 'recommended' | 'required'
export type UpdateReason = 'app' | 'contract' | 'chunk'

export interface PendingUpdate {
  level: UpdateLevel
  reason: UpdateReason
}

const bundleCommit = (): string | null => {
  const commit = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : ''
  return commit && commit !== 'dev' ? commit : null
}

/** Set when the API answers 404: the route is not deployed, so stop asking until the next load. */
let apiRouteMissing = false

const readJson = async (url: string): Promise<{ status: number; body: Record<string, unknown> | null }> => {
  try {
    const response = await fetch(url, { cache: 'no-store', credentials: 'omit' })
    if (!response.ok) return { status: response.status, body: null }
    const body = await response.json()
    return { status: response.status, body: body && typeof body === 'object' ? (body as Record<string, unknown>) : null }
  } catch {
    return { status: 0, body: null }
  }
}

const fetchAppCommit = async (): Promise<string | null> => {
  const { body } = await readJson(APP_VERSION_PATH)
  const commit = body?.commit
  return typeof commit === 'string' && commit && commit !== 'dev' ? commit : null
}

const fetchContract = async (): Promise<string | null> => {
  if (apiRouteMissing) return null
  const { status, body } = await readJson(`${getApiOrigin()}${API_VERSION_PATH}`)
  if (status === 404) apiRouteMissing = true
  if (!body) return null
  const data = (body.data && typeof body.data === 'object' ? body.data : body) as Record<string, unknown>
  const signature = data.contract ?? data.contractSignature ?? data.contractHash
  return typeof signature === 'string' && signature ? signature : null
}

const isChunkLoadError = (reason: unknown): boolean => {
  const message = reason instanceof Error ? reason.message : String(reason ?? '')
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(message)
}

export function useVersionCheck() {
  const [appUpdate, setAppUpdate] = useState<string | null>(null)
  const [contractChanged, setContractChanged] = useState(false)
  const [chunkFailed, setChunkFailed] = useState(false)
  const [dismissedCommit, setDismissedCommit] = useState<string | null>(null)
  const [requiredSnoozedUntil, setRequiredSnoozedUntil] = useState(0)
  /* Bumped when a snooze runs out, so the notice comes back on time. */
  const [, setTick] = useState(0)

  const baselineCommit = useRef<string | null>(bundleCommit())
  const baselineContract = useRef<string | null>(null)
  const lastCheck = useRef(0)
  const running = useRef(false)

  const check = useCallback(async (force = false) => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const now = Date.now()
    if (!force && now - lastCheck.current < MIN_GAP_MS) return
    if (running.current) return
    running.current = true
    lastCheck.current = now

    try {
      const [commit, contract] = await Promise.all([fetchAppCommit(), fetchContract()])

      if (commit) {
        if (!baselineCommit.current) baselineCommit.current = commit
        else if (commit !== baselineCommit.current) setAppUpdate(commit)
      }

      if (contract) {
        if (!baselineContract.current) baselineContract.current = contract
        else if (contract !== baselineContract.current) setContractChanged(true)
      }
    } finally {
      running.current = false
    }
  }, [])

  useEffect(() => {
    void check(true)

    const interval = window.setInterval(() => void check(true), CHECK_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    const onOnline = () => void check()
    /* Vite fires this when a lazily loaded piece of the bundle is gone, which
       after a deploy means the tab is older than the server. */
    const onPreloadError = (event: Event) => {
      event.preventDefault()
      setChunkFailed(true)
    }
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) setChunkFailed(true)
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    window.addEventListener('vite:preloadError', onPreloadError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('vite:preloadError', onPreloadError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [check])

  useEffect(() => {
    const remaining = requiredSnoozedUntil - Date.now()
    if (remaining <= 0) return
    const timer = window.setTimeout(() => setTick((value) => value + 1), remaining + 50)
    return () => window.clearTimeout(timer)
  }, [requiredSnoozedUntil])

  let pending: PendingUpdate | null = null
  if (chunkFailed || contractChanged) {
    if (Date.now() >= requiredSnoozedUntil) {
      pending = { level: 'required', reason: chunkFailed ? 'chunk' : 'contract' }
    }
  } else if (appUpdate && appUpdate !== dismissedCommit) {
    pending = { level: 'recommended', reason: 'app' }
  }

  const dismiss = useCallback(() => {
    if (chunkFailed || contractChanged) {
      setRequiredSnoozedUntil(Date.now() + REQUIRED_SNOOZE_MS)
    } else {
      setDismissedCommit(appUpdate)
    }
  }, [chunkFailed, contractChanged, appUpdate])

  return { pending, dismiss }
}
